import { SortableTableHeader } from '@avanta/shared/components/PaginatedTable/components/SortableTableHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avanta/shared/components/ui/Table';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { getCenterName, getStatusBadge } from '@avanta/shared/utils/appointments';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SESSION_DISPLAY_FORMAT } from 'constants/date';
import { type Appointment } from 'types/appointments';
import { type Report } from 'types/files';
import { formatUnifiedForDisplay } from 'utils/date';
import { SessionTableActions } from '../AppointmentsHistory/SessionTableActions';

interface AssociatedSessionsProps {
  sessions: Appointment[];
  reports: Report[];
}

const getColumns = (reports: Report[]): ColumnDef<Appointment>[] => [
  {
    id: 'dateTime',
    accessorKey: 'dateTime',
    enableSorting: true,
    header: (): string => t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string =>
      formatUnifiedForDisplay(row.original.dateTime, SESSION_DISPLAY_FORMAT),
  },
  {
    id: 'psychologist',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.psychologist.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string =>
      `${row.original.psychologist?.psychologist?.name ?? ''} ${row.original.psychologist?.psychologist?.surname ?? ''}`.trim(),
  },
  {
    id: 'type',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.type.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string =>
      t(`AppointmentCard.types.${row.original.bookedContactMethod}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
  },
  {
    id: 'status',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): React.ReactElement => getStatusBadge(row.original),
  },
  {
    id: 'center',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): string => getCenterName(row.original.bookedContactMethod, row.original.center),
  },
  {
    id: 'actions',
    enableSorting: false,
    header: (): string => t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }): React.ReactElement => (
      <SessionTableActions appointment={row.original} reports={reports} />
    ),
  },
];

export const AssociatedSessions = ({
  sessions,
  reports,
}: AssociatedSessionsProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'dateTime', desc: true }]);
  const columns = useMemo(() => getColumns(reports), [reports]);

  const table = useReactTable({
    data: sessions,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="border-beige-200 border-t px-4 py-4 am-lg:px-6">
      <p className="text-denim-800 mb-3 text-sm leading-4.5 font-medium">
        {t('PatientRecords.associatedSessions.heading', { count: sessions.length })}
      </p>
      {sessions.length === 0 ? (
        <p className="text-gray-500 text-sm">{t('PatientRecords.associatedSessions.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    <SortableTableHeader header={header} />
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
